import { createContext, useContext } from "react";
import { KnowledgeSearchResult, KnowledgeSearchFilters } from "@/shared/lib/search/interfaces";

export type AppMode = "auto" | "search" | "chat";

export type QueryState =
  | { phase: "idle"; appMode: AppMode }
  | { phase: "classifying" }
  | { phase: "searching" }
  | { phase: "search-results" }
  | { phase: "chat" };

export interface QueryControllerValue {
  /** Single state variable encoding both the query lifecycle phase and (when idle) the user's mode selection. */
  state: QueryState;
  /** Update the app mode. Only takes effect when idle. No-op when search is unavailable. */
  setAppMode: (mode: AppMode) => void;
  /**
   * Search results (empty if chat or not yet searched).
   * CR-013: row-based Knowledge Query API results — was `SearchDocWithContent[]`
   * (chat-document-search) before Search UI moved out of EE, see SOL-013 §3.4.
   */
  searchResults: KnowledgeSearchResult[];
  /** User-facing error message from the last search or classification request, null when idle */
  error: string | null;
  /** Submit a query - routes to search or chat based on app mode */
  submit: (
    query: string,
    onChat: (query: string) => void,
    filters?: KnowledgeSearchFilters
  ) => Promise<void>;
  /** Re-run the current search query with updated server-side filters */
  refineSearch: (filters: KnowledgeSearchFilters) => Promise<void>;
  /** Reset all state to initial values */
  reset: () => void;
}

export const QueryControllerContext = createContext<QueryControllerValue>({
  state: { phase: "idle", appMode: "chat" },
  setAppMode: () => undefined,
  searchResults: [],
  error: null,
  submit: async (_q, onChat) => {
    onChat(_q);
  },
  refineSearch: async () => undefined,
  reset: () => undefined,
});

export function useQueryController(): QueryControllerValue {
  return useContext(QueryControllerContext);
}
